import { createApi, BaseQueryFn, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { AxiosInstance, AxiosRequestConfig, AxiosError, Method } from 'axios'
import qs from 'qs'

const paramsSerializer = (params: any): string => qs.stringify(params, { arrayFormat: 'repeat' })

export class HttpService {
  readonly axiosInstance: AxiosInstance

  constructor(axiosInstance: AxiosInstance) {
    this.axiosInstance = axiosInstance
  }

  async request<Req, Resp>(url: string, method: Method, data: Req, headers: any = {}): Promise<Resp> {
    const body = data ?? null

    return (
      await this.axiosInstance({
        url,
        method,
        ...(body !== null && { data: body }),
        headers,
      })
    ).data
  }

  async post<Req, Resp>(url: string, request: Req, headers: any = {}): Promise<Resp> {
    return (
      await this.axiosInstance({
        method: 'post',
        url,
        data: request ?? undefined,
        headers,
      })
    ).data
  }

  async get<Resp>(url: string, headers: any = {}, params: any = {}): Promise<Resp> {
    return (
      await this.axiosInstance({
        method: 'get',
        url,
        headers,
        params: params ?? undefined,
        paramsSerializer: {
          serialize: paramsSerializer,
        },
      })
    ).data
  }

  async put<Req, Resp>(url: string, request: Req, headers: any = {}): Promise<Resp> {
    return (
      await this.axiosInstance({
        method: 'put',
        url,
        data: request ?? undefined,
        headers,
      })
    ).data
  }

  async delete<Resp>(url: string, headers: any = {}): Promise<Resp> {
    return (await this.axiosInstance({ method: 'delete', url, headers })).data
  }
}

export class ApiService {
  public baseQueryFn: HttpService | null = null

  // @ts-ignore
  readonly getBaseQuery: BaseQueryFn<
    {
      url: string
      method: Method
      data?: AxiosRequestConfig['data']
    },
    unknown,
    FetchBaseQueryError
  > = async (args) => {
    const { url, method, data } = args
    try {
      if (this.baseQueryFn === null) {
        throw new Error('baseQueryFn not set')
      }
      const result = await this.baseQueryFn.request(url, method, data ?? null)
      return { data: result }
    } catch (e) {
      const error = e as AxiosError & Error
      const response = error.response?.data ?? error.message ?? null
      return { error: { status: error.response?.status, data: response } }
    }
  }

  public setBaseQueryFn(queryFn: HttpService): ApiService {
    this.baseQueryFn = queryFn
    return this
  }

  readonly service = createApi({
    reducerPath: 'shop-api',
    baseQuery: this.getBaseQuery,
    endpoints: () => ({}),
  })
}

export const apiService = new ApiService()
